import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Prompt } from "react-router-dom";
import mammoth from "mammoth/mammoth.browser";

import PlanDataService from "../services/plan.service";
import AuthService from "../services/auth.service";
import ReviewList, { scopeReviews } from "./review-list.component";
import ReviewDataService from "../services/review.service";
import LessonFileManager from "./lesson-file-manager.component";
import AskAiMenu, { askAiPresets, basicInfoPresets } from "./ask-ai-menu.component";
import { PLAN_THEMES, PLAN_GRADES, PLAN_SEASONS, PLAN_STATUSES, EMPTY_LESSON, currentSeason } from "../constants/plan-options";
import { consumeSkipUnsavedWarning } from "../utils/unsavedChangesGuard";
import { registerSessionExpiryHandler, writeLocalDraft, takeLocalDraft, isDraftStale } from "../utils/sessionExpiryGuard";
import { hasPendingReviewEdits, discardPendingReviewEdits, subscribePendingReviewEdits } from "../utils/pendingReviewEdits";
import { registerCopilotSaveHandler } from "../utils/copilotSaveGuard";
import "../curriculum.css";
import { useExplorerPane, ExplorerPaneBar, isPaneSwitch } from "./explorer-pane";
import { tr } from "../i18n";

// True if an answers object (shaped like planFormData/one executionFormData
// record) has at least one non-empty field, across however many sections the
// schema has -- used for the "提交待点评" not-empty gate below. Previously
// imported from utils/planDocExtract.js, which also used it (before that
// file's extraction moved server-side, see backend/app/services/
// planDocExtract.js) to decide whether an upload actually matched anything;
// kept here as a small local helper since this particular use has nothing to
// do with uploads.
const hasAnySectionContent = (schema, answers) => {
  const sections = (schema && schema.sections) || [];
  const hasValue = (obj) => Object.values(obj || {}).some((v) => v != null && String(v).trim() !== "");
  if (sections.length > 1) return sections.some((s) => hasValue(answers && answers[s.key]));
  return hasValue(answers);
};

// planFormData is nested by section key when its schema has more than one
// section (plan_design's seed: why/what/how), or flat when it has exactly
// one (any future re-uploaded template collapses to one section -- see the
// dynamic-templates plan) -- matches backend/app/services/planDocExtract.js#
// buildAnswersFromExtracted/dynamicDocGenerator.js's sectionAnswers exactly,
// so save/render/generate all agree on the same shape. `schema` is
// plan.PlanTemplateVersion.schemaJson.
const mergeFormData = (data, schema) => {
  const sections = (schema && schema.sections) || [];
  const lessons = Array.isArray(data && data.lessons) ? data.lessons : [];
  // Leftover fields templateMigration.js#migratePlanFormData stashed outside
  // any schema section -- must be carried through exactly like `lessons`
  // above (a single-section schema's own `{ ...data, lessons }` spread
  // already keeps it; the multi-section branch below builds `merged` from
  // scratch and would otherwise silently drop it on the very next "保存草稿",
  // since planFormData is always saved wholesale from this state -- see
  // saveAll).
  const manualMigration = Array.isArray(data && data._manualMigration) ? data._manualMigration : undefined;
  if (sections.length > 1) {
    const merged = { lessons };
    if (manualMigration) merged._manualMigration = manualMigration;
    sections.forEach((s) => {
      merged[s.key] = { ...(data && data[s.key]) };
    });
    return merged;
  }
  return { ...(data || {}), lessons };
};

// "Anchoring level" -- the granularity at which the online form splits into
// separate sidebar pages and gets its own segment-level expert review block
// (see ReviewList's sectionKey prop below). For a heading-style-parsed
// template with a single top-level wrapper (see templateParser.js#
// parseHeadingSections) -- e.g. "WHY ·学习目标" nested one level under a lone
// top-level "课程设计框架" (confirmed on two real templates) -- that wrapper
// itself is never a real page (it has no content of its own, purely a
// grouping label), so this pulls its immediate subsections out as the real
// pages instead of lumping WHY/WHAT/HOW into one page under it.
//
// Unwrapping only fires when there's *exactly one* top-level section,
// though -- a third real template has no such wrapper at all (基本信息/WHY/
// WHAT/HOW/分课时设计 are all separate top-level Heading1s, with HOW's own
// 入项/探究/制作与迭代/出项 nested under it as Heading2). There, WHY/WHAT/HOW
// are already at the right granularity as top-level sections, exactly like
// the hand-authored seed and every table/flat-parsed schema -- and HOW must
// stay intact as one page (with its own subsections grouped inside it, per
// "same anchoring level -> same page"), not itself be unwrapped just because
// it happens to have subsections while its siblings don't. Deciding this per
// schema (sections.length) rather than per individual section is what makes
// that distinction correctly.
const anchorSections = (schema) => {
  const sections = (schema && schema.sections) || [];
  if (sections.length === 1 && sections[0].subsections && sections[0].subsections.length > 0) {
    return sections[0].subsections;
  }
  return sections;
};

// A node's own direct (non-descendant) fields: top-level schema.sections
// entries carry both `fields` (flattened, all descendants, kept for legacy
// flat-shape consumers -- see templateParser.js) and `ownFields` (direct-
// only); every other node (a nested subsection, or an anchor pulled from
// one) only ever has `fields`, which is already direct-only by construction.
const directFields = (section) => (section.ownFields !== undefined ? section.ownFields : section.fields || []);

// Template labels often end in a colon ("认知思维目标：") -- dropped where a
// label is quoted as a name, as in 就「认知思维目标」提问.
const bareLabel = (label) => String(label || "").replace(/[：:]\s*$/, "");

// A whole section/subsection's answers as one text block for 欣欣小助手's
// focus (see AskAiMenu) -- "【label】value" per field, outline order, with
// untouched fields marked rather than showing their template hint.
const partDraftText = (fields, subsections, values) => {
  const lines = [];
  const walk = (fs, subs, prefix) => {
    (fs || []).forEach((f) => {
      const label = [...prefix, f.group, f.label].filter(Boolean).join(" · ");
      const v = values && values[f.key];
      lines.push(v != null && String(v).trim() ? `【${label}】\n${v}` : `【${label}】（未填写）`);
    });
    (subs || []).forEach((sub) => walk(sub.fields, sub.subsections, [...prefix, sub.label]));
  };
  walk(fields, subsections, []);
  return lines.join("\n\n");
};

// The freeform 分课时设计 form's two fields (no lessonSchema), in the same
// shape as a schema's fields -- for partDraftText.
const FREEFORM_LESSON_FIELDS = [
  { key: "title", label: "课时标题" },
  { key: "content", label: "课时设计内容" },
];

// Renders one schema section's fields as labeled textareas, grouping
// consecutive same-`group` fields under one sub-heading and repeating
// "{group} · {label}" on each (same convention as HOW's own nested fields
// and 实施记录's 教学活动流程 -- see dynamicDocGenerator.js's identical
// grouping logic on the doc-generation side). Shared by every online-fill
// section (WHY/WHAT/HOW-equivalent and 实施记录-equivalent alike) instead of
// each hand-rolling its own field list.
//
// `subsections`, when present (a heading-style-parsed template -- see
// backend/app/services/templateParser.js#parseHeadingSections), recurses one
// level per nested heading, indented by `depth`, so a template's real multi-
// level outline (e.g. WHY/WHAT/HOW -> 最终成果 -> 个人成果/团队成果) renders as
// true nested groups rather than collapsing to a single `group` label.
// `values` stays the one flat per-top-level-section answers object at every
// depth -- field keys are globally unique across the whole schema (see
// onFormFieldChange/mergeFormData), so no per-depth answer namespacing is
// needed.
//
// `askAi`, when given, puts a 小助手 hover menu (AskAiMenu) on every
// field and subsection heading: { kind, basePath (labels of the enclosing
// page), sectionKey?, lessonIndex?, presets, getValues } -- getValues()
// returns this part's *live* answers, so a question sees unsaved edits.
const DynamicSectionFields = ({ fields, subsections, values, canEdit, onFieldChange, askAi, depth = 0 }) => {
  let lastGroup;
  const focusBase = askAi && { kind: askAi.kind, sectionKey: askAi.sectionKey, lessonIndex: askAi.lessonIndex };
  return (
    <>
      {(fields || []).map((field) => {
        const isNewGroup = field.group && field.group !== lastGroup;
        lastGroup = field.group;
        return (
          <React.Fragment key={field.key}>
            {isNewGroup && <h6 className="mt-3 mb-2">{field.group}</h6>}
            <div className={`form-group${askAi ? " ai-ask-host" : ""}`}>
              <label>{field.group ? `${field.group} · ${field.label}` : field.label}</label>
              {askAi && (
                <AskAiMenu
                  presets={askAi.presets}
                  target={{
                    ...focusBase,
                    fieldKey: field.key,
                    labelPath: [...askAi.basePath, ...(field.group ? [bareLabel(field.group)] : []), bareLabel(field.label)],
                    // Same text the textarea shows (hint while untouched).
                    getDraft: () => {
                      const v = (askAi.getValues() || {})[field.key];
                      return v != null ? String(v) : field.hint || "";
                    },
                  }}
                />
              )}
              <textarea
                className="form-control"
                rows="4"
                // field.hint only fills in while the field is genuinely
                // untouched (null/undefined) -- an explicit "" (the teacher
                // deliberately cleared it) stays blank rather than snapping
                // back. Rendered as real textarea content, not a `placeholder`
                // (which is native "vanishes on the first keystroke" browser
                // behavior) -- typing now edits within/around the hint text
                // instead of erasing it, and that first onChange carries it
                // forward into real saved state.
                value={values && values[field.key] != null ? values[field.key] : field.hint || ""}
                disabled={!canEdit}
                onChange={(e) => onFieldChange(field.key, e.target.value)}
              />
            </div>
          </React.Fragment>
        );
      })}
      {(subsections || []).map((sub) => (
        <div key={sub.key} style={{ marginLeft: depth * 16 }}>
          <h6 className={`mt-3 mb-2${askAi ? " ai-ask-host" : ""}`}>
            {sub.label}
            {askAi && (
              <AskAiMenu
                presets={askAi.presets}
                unit={tr("这一部分")}
                target={{
                  ...focusBase,
                  labelPath: [...askAi.basePath, bareLabel(sub.label)],
                  getDraft: () => partDraftText(sub.fields, sub.subsections, askAi.getValues()),
                }}
              />
            )}
          </h6>
          <DynamicSectionFields
            fields={sub.fields}
            subsections={sub.subsections}
            values={values}
            canEdit={canEdit}
            onFieldChange={onFieldChange}
            askAi={askAi && { ...askAi, basePath: [...askAi.basePath, bareLabel(sub.label)] }}
            depth={depth + 1}
          />
        </div>
      ))}
    </>
  );
};

// The header's 总体/分课时设计完成度 and 实施完成度 (see planCompletion.js).
// Sent only to the owner and
// admins/experts (plan.controller.js#findOne's canSeeCompletion) -- a peer
// teacher never gets it, so this renders nothing for them. As of the last
// save (retrievePlan reloads it), or of the last 支撑材料 upload/delete
// (refreshCompletion). (AI 设计分数 sits on its AI 点评 row, see
// review-list.component.js.) Its own component rather than inline JSX:
// PlanDetail is big enough that one more conditional branch in it trips
// react-hooks/rules-of-hooks' path counting into a false "hook called
// conditionally" error, which fails the dev build.
const CompletionBadge = ({ completion }) =>
  completion ? (
    <span
      className="pl-completion"
      title={tr("总体设计 = 基本信息 {completion.basic}% · 课程设计 {completion.design}%；分课时设计、实施各共 {completion.lessonCount} 课时，每课时实施 = 实施记录 50% + 支撑材料 50%（按上次保存的内容计算）", { "completion.basic": completion.basic, "completion.design": completion.design, "completion.lessonCount": completion.lessonCount })}
    >
      {tr("总体设计完成度：{completion.general}% · 分课时设计完成度：{completion.lessonDesign}% · 实施完成度：{completion.execution}%", { "completion.general": completion.general, "completion.lessonDesign": completion.lessonDesign, "completion.execution": completion.execution })}
    </span>
  ) : null;

// The page's single 保存草稿/提交待点评 pair, rendered once in the sticky
// header (see .pl-sticky-header) rather than above and below every section
// -- it saves everything dirty across all sections at once (see saveAll),
// so it stays reachable however far down a long section the teacher has
// scrolled. showSubmit is false once the plan has left "draft" (submitted/
// reviewed) -- 提交待点评 doesn't apply anymore at that point, so it's dropped
// entirely rather than left showing disabled, same as every other
// case-content action in this file that's owner/draft-gated by omission.
//
// A disabled button says why on hover -- greyed out with no explanation
// read as "the plan got submitted". The tooltip sits on a wrapping span:
// a disabled <button> gets no mouse events, so its own title may never show
// (pointer-events: none lets the hover reach the span, per Bootstrap's docs).
const SaveSubmitButtons = ({ onSaveDraft, saveDisabled, onSubmit, submitDisabled, showSubmit = true }) => (
  <div className="d-flex">
    <span className="d-inline-block mr-2" title={saveDisabled ? tr("没有未保存的修改") : undefined}>
      <button className="btn btn-light" type="button" onClick={onSaveDraft} disabled={saveDisabled} style={saveDisabled ? { pointerEvents: "none" } : undefined}>
        {tr("保存草稿")}
      </button>
    </span>
    {showSubmit && (
      <span className="d-inline-block" title={submitDisabled ? tr("请先填写 WHY/WHAT/HOW、分课时设计或实施记录后再提交") : undefined}>
        <button className="btn btn-light" type="button" onClick={onSubmit} disabled={submitDisabled} style={submitDisabled ? { pointerEvents: "none" } : undefined}>
          {tr("提交待点评")}
        </button>
      </span>
    )}
  </div>
);

// Plan-level 课程设计文件 panel, reached from a single sidebar leaf (see
// PLAN_SECTIONS below). All three commands are shown at once:
// 下载/预览 act immediately on click; 上传 just toggles the drop-zone/browse
// UI open rather than acting itself, since it needs a file first. Nothing is
// materialized server-side for any of the three -- 下载/预览 both hit
// GET /plans/:id/design-doc (plan.controller.js#renderDoc), which renders the
// plan's *current* content into a .docx on the fly and streams it back, so
// there's no generated copy to go stale or to clean up; 上传 POSTs the
// dropped/picked .docx straight to that same path (plan.controller.js#
// uploadDesignDoc), which runs the best-effort extraction server-side (see
// backend/app/services/planDocExtract.js) and overwrites the plan's
// planFormData wholesale after an explicit confirm -- a destructive action,
// so it's gated behind a warning rather than a silent merge.
const DesignDocPanel = ({ planId, plan, canEdit, onUploadComplete }) => {
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(""); // "" | "download" | "preview"
  const [showUpload, setShowUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fileName = () => `${(plan && plan.title) || tr("乡土课程设计方案")}.docx`;

  const fetchDesignDocBuffer = async () => {
    const resp = await PlanDataService.downloadDesignDoc(planId);
    return resp.data;
  };

  const handleDownload = async () => {
    setMessage("");
    setWorking("download");
    try {
      const data = await fetchDesignDocBuffer();
      const url = window.URL.createObjectURL(
        new Blob([data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.log(e);
      setMessage(tr("下载失败。"));
    } finally {
      setWorking("");
    }
  };

  // Opens a blank window synchronously, before the first await, so the
  // browser attributes it to this click and doesn't treat it as a
  // popup-blocked async open -- then fills it in once the doc's rendered.
  // Same pattern as lesson-file-manager.component.js's openPreview.
  const handlePreview = async () => {
    setMessage("");
    setWorking("preview");
    const win = window.open("", "_blank");
    if (win) win.document.write(`<title>${tr("预览：")}${fileName()}</title><body>${tr("预览加载中...")}</body>`);
    try {
      const data = await fetchDesignDocBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: data });
      if (win) {
        win.document.open();
        win.document.write(
          `<!doctype html><html><head><meta charset="utf-8"><title>${tr("预览：")}${fileName()}</title>` +
            `<style>body{max-width:800px;margin:24px auto;padding:0 16px;font-family:sans-serif;line-height:1.6;}</style>` +
            `</head><body>${result.value || tr("<p>文档内容为空。</p>")}</body></html>`
        );
        win.document.close();
      }
    } catch (e) {
      console.log(e);
      setMessage(tr("预览失败。"));
      if (win) win.close();
    } finally {
      setWorking("");
    }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (ext !== "docx") {
      setMessage(tr("仅支持上传 .docx 文件。"));
      return;
    }
    if (
      !window.confirm(
        tr("上传新文件将覆盖当前课程设计方案的全部在线内容（WHY/WHAT/HOW 及分课时设计），且无法撤销，确定继续吗？")
      )
    ) {
      return;
    }
    setMessage("");
    setIsUploading(true);
    try {
      // Extraction (mammoth + label matching against this plan's own pinned
      // schema) now runs entirely server-side -- see backend/app/services/
      // planDocExtract.js and plan.controller.js#uploadDesignDoc -- so this
      // just ships the raw file and refreshes once the backend applies it.
      await PlanDataService.uploadDesignDoc(planId, file);
      await onUploadComplete("plan");
      setMessage(tr("课程设计文件已上传，在线内容已更新。"));
      setShowUpload(false);
    } catch (e) {
      console.log(e);
      setMessage((e && e.response && e.response.data && e.response.data.message) || tr("上传失败，请确认文件格式。"));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      {message && <div className="alert alert-info py-2">{message}</div>}

      <div className="mb-2">
        {canEdit && (
          <button
            className={`btn btn-sm mr-2 ${showUpload ? "btn-primary" : "btn-outline-primary"}`}
            type="button"
            onClick={() => setShowUpload((v) => !v)}
          >
            {tr("上传")}
          </button>
        )}
        <button className="btn btn-outline-primary btn-sm mr-2" type="button" onClick={handleDownload} disabled={working === "download"}>
          {working === "download" ? tr("下载中...") : tr("下载")}
        </button>
        <button className="btn btn-outline-primary btn-sm" type="button" onClick={handlePreview} disabled={working === "preview"}>
          {working === "preview" ? tr("生成中...") : tr("预览")}
        </button>
      </div>
      <div className="text-muted small mb-3">
        {tr("“下载”“预览”均根据课程设计方案的当前在线内容实时生成，不保存文件，每次都反映最新内容；预览将在新窗口中打开。")}
      </div>

      {showUpload && canEdit && (
        <div className="form-group">
          <label>{tr("上传课程设计文件（将覆盖当前在线内容）")}</label>
          <div
            className={`pl-file-drop-zone ${dragActive ? "is-dragover" : ""}`}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleUploadFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
            }}
          >
            {isUploading ? tr("正在解析并上传...") : tr("拖拽 .docx 文件到这里，或点击选择文件以覆盖当前内容")}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            className="d-none"
            onChange={(e) => handleUploadFile(e.target.files[0])}
          />
          <small className="form-text text-muted">{tr("上传的文件将替换当前的 WHY/WHAT/HOW 及分课时设计内容，此操作无法撤销。")}</small>
        </div>
      )}
    </div>
  );
};

// Per-课时 课程实施文件 panel -- same 上传/下载/预览 pattern as DesignDocPanel
// above, scoped to one lesson's 实施记录 (plan.executionFormData) instead of
// the plan's own WHY/WHAT/HOW. 下载/预览 both hit
// GET /plans/:id/lessons/:lessonIndex/execution-doc
// (plan.controller.js#renderExecutionDoc), rendered on the fly from the
// lesson's current 实施记录 entry and never persisted. 上传 POSTs the
// dropped/picked .docx straight to that same path
// (plan.controller.js#uploadExecutionDoc), which extracts it server-side
// (driven by plan.ExecutionTemplateVersion's schema) and overwrites just
// this lesson's entry in plan.executionFormData, gated behind an explicit
// confirm, same as DesignDocPanel's 上传.
const LessonExecutionDocPanel = ({ planId, lessonIndex, plan, canEdit, onUploadComplete }) => {
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(""); // "" | "download" | "preview"
  const [showUpload, setShowUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fileName = () => tr("{0}-课时{lessonIndex}-实施记录.docx", { "0": (plan && plan.title) || tr("乡土课程设计方案"), lessonIndex });

  const fetchExecutionDocBuffer = async () => {
    const resp = await PlanDataService.downloadExecutionDoc(planId, lessonIndex);
    return resp.data;
  };

  const handleDownload = async () => {
    setMessage("");
    setWorking("download");
    try {
      const data = await fetchExecutionDocBuffer();
      const url = window.URL.createObjectURL(
        new Blob([data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.log(e);
      setMessage(tr("下载失败。"));
    } finally {
      setWorking("");
    }
  };

  const handlePreview = async () => {
    setMessage("");
    setWorking("preview");
    const win = window.open("", "_blank");
    if (win) win.document.write(`<title>${tr("预览：")}${fileName()}</title><body>${tr("预览加载中...")}</body>`);
    try {
      const data = await fetchExecutionDocBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: data });
      if (win) {
        win.document.open();
        win.document.write(
          `<!doctype html><html><head><meta charset="utf-8"><title>${tr("预览：")}${fileName()}</title>` +
            `<style>body{max-width:800px;margin:24px auto;padding:0 16px;font-family:sans-serif;line-height:1.6;}</style>` +
            `</head><body>${result.value || tr("<p>文档内容为空。</p>")}</body></html>`
        );
        win.document.close();
      }
    } catch (e) {
      console.log(e);
      setMessage(tr("预览失败。"));
      if (win) win.close();
    } finally {
      setWorking("");
    }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (ext !== "docx") {
      setMessage(tr("仅支持上传 .docx 文件。"));
      return;
    }
    if (!window.confirm(tr("上传新文件将覆盖本课时当前的实施记录内容，且无法撤销，确定继续吗？"))) {
      return;
    }
    setMessage("");
    setIsUploading(true);
    try {
      // Extraction + merging into this lesson's slot of executionFormData
      // now happens entirely server-side (reading the plan's own current,
      // authoritative executionFormData column, not a client-supplied
      // in-memory array) -- see backend/app/services/planDocExtract.js and
      // plan.controller.js#uploadExecutionDoc.
      await PlanDataService.uploadExecutionDoc(planId, lessonIndex, file);
      await onUploadComplete("execution");
      setMessage(tr("课程实施文件已上传，实施记录已更新。"));
      setShowUpload(false);
    } catch (e) {
      console.log(e);
      setMessage((e && e.response && e.response.data && e.response.data.message) || tr("上传失败，请确认文件格式。"));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      {message && <div className="alert alert-info py-2">{message}</div>}

      <div className="mb-2">
        {canEdit && (
          <button
            className={`btn btn-sm mr-2 ${showUpload ? "btn-primary" : "btn-outline-primary"}`}
            type="button"
            onClick={() => setShowUpload((v) => !v)}
          >
            {tr("上传")}
          </button>
        )}
        <button className="btn btn-outline-primary btn-sm mr-2" type="button" onClick={handleDownload} disabled={working === "download"}>
          {working === "download" ? tr("下载中...") : tr("下载")}
        </button>
        <button className="btn btn-outline-primary btn-sm" type="button" onClick={handlePreview} disabled={working === "preview"}>
          {working === "preview" ? tr("生成中...") : tr("预览")}
        </button>
      </div>
      <div className="text-muted small mb-3">
        {tr("“下载”“预览”均根据本课时的当前实施记录实时生成，不保存文件，每次都反映最新内容；预览将在新窗口中打开。")}
      </div>

      {showUpload && canEdit && (
        <div className="form-group">
          <label>{tr("上传课程实施文件（将覆盖本课时当前的实施记录）")}</label>
          <div
            className={`pl-file-drop-zone ${dragActive ? "is-dragover" : ""}`}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleUploadFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
            }}
          >
            {isUploading ? tr("正在解析并上传...") : tr("拖拽 .docx 文件到这里，或点击选择文件以覆盖当前内容")}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            className="d-none"
            onChange={(e) => handleUploadFile(e.target.files[0])}
          />
          <small className="form-text text-muted">{tr("上传的文件将替换本课时当前的实施记录内容，此操作无法撤销。")}</small>
        </div>
      )}
    </div>
  );
};

// Migrated from shinshin's case-detail.component.js, with the online-fill WHY/WHAT/HOW form
// (matching curriculum_template/乡土课程设计方案模版.docx's structure). Layout: a file-explorer
// style split -- a collapsible left nav tree (计划/its sections, 实施/its 课时 segments each
// admin-only 管理 leaf) drives a single-section content pane on the right, replacing the old
// waterfall of every card stacked vertically (and the react-tabs 课时 block) with one section
// visible at a time.
// WHY/WHAT/HOW-equivalent leaves aren't listed here -- they're driven
// entirely by plan.PlanTemplateVersion.schemaJson.sections at render time
// (see the "planSection" branch and the sidebar below), same as
// 分课时设计/实施记录 already were section content, not a fixed list. Both
// online and upload-mode plans get the same static leaves; only the
// schema-driven sections and 分课时设计 are gated to planMode === "online".
const PLAN_STATUS_LABELS = Object.fromEntries(PLAN_STATUSES.map((s) => [s.value, s.label]));

const PLAN_SECTIONS = [
  { key: "basic", label: tr("基本信息") },
  { key: "files", label: tr("课程设计文件") },
  { key: "reviews", label: tr("计划整体点评") },
];

// What the phone layout's bar over the content says is open (see
// explorer-pane.js) -- the tree entry's own wording, with its 课时 where the
// entry alone ("实施记录") wouldn't say which.
const paneLabelOf = (selected, anchorSections) => {
  const n = selected.key;
  switch (selected.type) {
    case "plan":
      return (PLAN_SECTIONS.find((s) => s.key === n) || { label: tr("计划") }).label;
    case "planSection":
      return (anchorSections.find((s) => s.key === n) || { label: tr("计划") }).label;
    case "planManualMigration":
      return tr("手动迁移内容");
    case "planLesson":
      return tr("分课时设计 · 课时 {n}", { n });
    case "executionDoc":
      return tr("课时 {n} · 课程实施文件", { n });
    case "executionRecord":
      return tr("课时 {n} · 实施记录", { n });
    case "execution":
      return tr("课时 {n} · 支撑材料", { n });
    case "executionReviews":
      return tr("实施整体点评");
    case "admin":
      return tr("管理");
    default:
      return tr("目录");
  }
};

// A ?view= link opens on that section, on a phone too (explorer-pane.js).
const linksToSection = (location) => !!new URLSearchParams((location && location.search) || "").get("view");

// 基本信息 fields in form order, for 欣欣小助手's focus text.
const BASIC_INFO_LABELS = [
  ["title", "标题"],
  ["year", "年份"],
  ["season", "学季"],
  ["theme", "乡土主题"],
  ["grade", "年级"],
  ["studentCount", "学生人数"],
  ["instructorName", "执教人"],
  ["plannedLessonCount", "预计课时"],
];

// 基本信息 form -> PlanDataService.update payload. Shared by saveAll and the
// session-timeout auto-save, so both send the same fields.
const metaFormPayload = (metaForm) => ({
  title: metaForm.title,
  theme: metaForm.theme || null,
  grade: metaForm.grade || null,
  studentCount: metaForm.studentCount ? Number(metaForm.studentCount) : null,
  instructorName: metaForm.instructorName || null,
  year: Number(metaForm.year),
  season: metaForm.season || null,
  plannedLessonCount: metaForm.plannedLessonCount ? Number(metaForm.plannedLessonCount) : null,
});

// Local fallback for edits a session timeout couldn't get to the server
// (see the session-expiry handler in PlanDetail and utils/sessionExpiryGuard.js).
const planDraftKey = (planId, userId) => `planDraft:${planId}:${userId}`;
const writePlanDraft = (planId, userId, parts) => writeLocalDraft(planDraftKey(planId, userId), parts);
const takePlanDraft = (planId, userId) => takeLocalDraft(planDraftKey(planId, userId));

const PlanDetail = (props) => {
  const planId = props.match.params.id;
  const [plan, setPlan] = useState(null);
  // sectionKey -> real anchor label (e.g. "S0" -> "WHY ·学习目标") -- a
  // heading-style-parsed template's anchors get auto-generated keys (see
  // anchorSections/section.key below), meaningless on their own in review-
  // list.component.js's "点评（...）" header, 模块 column, and aggregate-view
  // filtering. Memoized on the plan's pinned template id (stable for the
  // whole editing session) instead of recomputed fresh every render --
  // ReviewList depends on this object's identity in a fetch-triggering
  // useCallback, so a fresh object every render would refetch its review
  // list on every keystroke elsewhere on the page. Declared here, before any
  // of this component's early returns below, per the Rules of Hooks.
  const planSectionLabels = useMemo(() => {
    const schema = (plan && plan.PlanTemplateVersion && plan.PlanTemplateVersion.schemaJson) || { sections: [] };
    return Object.fromEntries(anchorSections(schema).map((s) => [s.key.toUpperCase(), s.label]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan && plan.PlanTemplateVersion && plan.PlanTemplateVersion.id]);
  // 乡土主题 dropdown options -- defaults to the static PLAN_THEMES list
  // (matches today's behavior, and covers a slow/failed fetch) then swapped
  // for the active plan_design template's own "附件"-derived list, if it has
  // one (see plan.controller.js#getOptions/templateParser.js#
  // extractThemeOptionsFromFields) -- a template can define its own theme
  // taxonomy without a code change.
  const [themeOptions, setThemeOptions] = useState(PLAN_THEMES);
  useEffect(() => {
    PlanDataService.getOptions()
      .then((resp) => {
        if (Array.isArray(resp.data && resp.data.themes) && resp.data.themes.length > 0) setThemeOptions(resp.data.themes);
      })
      .catch(() => {});
  }, []);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [message, setMessage] = useState("");
  const [metaForm, setMetaForm] = useState(null);
  // 基本信息 is always shown as a live editable form (no separate
  // view/edit toggle) -- seeded straight from the plan on every
  // retrievePlan() below.
  const [formData, setFormData] = useState({ lessons: [] });
  // Sparse array of 实施记录 entries, one per 课时, keyed by `index` -- same
  // shape as formData.lessons (see onLessonFieldChange), just plan-level
  // execution-record data instead of design content.
  const [executionFormData, setExecutionFormData] = useState([]);
  // Dirty tracking for the page-wide 保存草稿/提交待点评 pair (see saveAll)
  // -- planDirty covers formData (WHY/WHAT/HOW + 分课时设计),
  // executionDirty covers executionFormData (every 课时's 实施记录, sent
  // together as one array); only the dirty ones go into the save. 提交待
  // 点评 needs no dirty tracking of its own: it's gated purely by
  // `plan.status === "draft"` (permanently disabled the moment a plan is
  // ever submitted -- status only ever moves away from "draft", never
  // back) and by planNotEmpty/executionNotEmpty (don't submit genuinely
  // blank content) -- both computed straight from the plan's actual
  // current data, so a plan born with content (e.g. created via 从文件导入)
  // is correctly submittable immediately, with no separate "a save
  // happened in this browsing session" flag required.
  const [planDirty, setPlanDirty] = useState(false);
  const [executionDirty, setExecutionDirty] = useState(false);
  // Same idea as planDirty/executionDirty above, scoped to metaForm (基本信息)
  // instead, so an untouched 基本信息 isn't resent (and re-validated) on
  // every save.
  const [metaDirty, setMetaDirty] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  // Phone: the 计划/实施 tree or the picked section, one at a time.
  const pane = useExplorerPane({ startOpen: linksToSection(props.location) });
  // planLessons (分课时设计, nested under 计划) starts collapsed, unlike plan/
  // execution -- it can hold as many leaves as 实施's own 课时 list, and it's
  // one level deeper, so expanding it by default would make the plan section
  // of the sidebar as tall as the whole 实施 tree before a teacher's even
  // looked at it.
  const [expandedGroups, setExpandedGroups] = useState({ plan: true, execution: true, planLessons: false });
  // ?view=reviews / ?view=executionReviews opens straight onto 计划整体点评 /
  // 实施整体点评 -- used by 数据看板's 专家点评 links.
  const [selected, setSelected] = useState(() => {
    const view = new URLSearchParams((props.location && props.location.search) || "").get("view");
    if (view === "reviews") return { type: "plan", key: "reviews" };
    if (view === "executionReviews") return { type: "executionReviews" };
    return { type: "plan", key: "basic" };
  });
  // Lifted up from the two 整体点评 ReviewList widgets (design/implementation)
  // rather than left as their own local state -- switching sidebar tabs
  // unmounts/remounts those widgets, and an AI review request is a single
  // synchronous call that can keep running well after that; keeping the
  // "still generating" flag here, on a component that stays mounted for the
  // whole page, means "AI点评生成中..." survives clicking away and back
  // (see review-list.component.js's aiPending/setAiPending props).
  const [aiReviewPending, setAiReviewPending] = useState({ design: false, implementation: false });

  // Height of the sticky header (see .pl-sticky-header) -- the nav tree's
  // own sticky `top` sits just below it instead of sliding underneath.
  // Measured rather than fixed, since a long 标题 wraps onto more lines.
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const stickyHeaderObserver = useRef(null);
  const stickyHeaderRef = useCallback((node) => {
    if (stickyHeaderObserver.current) stickyHeaderObserver.current.disconnect();
    stickyHeaderObserver.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setStickyHeaderHeight(node.offsetHeight));
    observer.observe(node);
    stickyHeaderObserver.current = observer;
  }, []);

  const toggleGroup = (name) => setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  const select = (type, key) => setSelected({ type, key });

  // 支撑材料 uploads/deletes save immediately, outside 保存草稿 -- so pull
  // just the recomputed completion, leaving the rest of the plan (and any
  // unsaved edits in the forms) alone.
  const refreshCompletion = () => {
    PlanDataService.get(planId)
      .then((resp) => setPlan((prev) => (prev ? { ...prev, completion: resp.data.completion } : prev)))
      .catch(() => {});
  };

  const retrievePlan = useCallback(async () => {
    setIsLoadingPlan(true);
    try {
      const resp = await PlanDataService.get(planId);
      setPlan(resp.data);
      setMetaForm({
        title: resp.data.title || "",
        theme: resp.data.theme || "",
        grade: resp.data.grade || "",
        studentCount: resp.data.studentCount ? String(resp.data.studentCount) : "",
        instructorName: resp.data.instructorName || "",
        year: resp.data.year ? String(resp.data.year) : "",
        // Falls back to the current 学期 rather than "" -- covers both a
        // freshly-created plan and an older one from before this field
        // existed, so the select always shows a sensible value instead of
        // a blank "不限".
        season: resp.data.season || currentSeason(),
        plannedLessonCount: resp.data.plannedLessonCount ? String(resp.data.plannedLessonCount) : "",
      });
      setMetaDirty(false);
      setFormData(mergeFormData(resp.data.planFormData, resp.data.PlanTemplateVersion && resp.data.PlanTemplateVersion.schemaJson));
      setExecutionFormData(Array.isArray(resp.data.executionFormData) ? resp.data.executionFormData : []);

      // Edits stashed by a session timeout that never made it to the server
      // (see the session-expiry handler below) -- restored as still-unsaved
      // (dirty) so the teacher reviews them and saves explicitly. Consumed
      // on restore: from here on the usual beforeunload/<Prompt> guards and
      // a repeat timeout's own stash cover them. Dropped if the plan changed
      // server-side after the stash (e.g. edited later from another device),
      // so a stale draft never clobbers newer content (see isDraftStale).
      const user = AuthService.getCurrentUser();
      const draft = user && String(resp.data.teacherId) === String(user.id) ? takePlanDraft(planId, user.id) : null;
      if (draft && !isDraftStale(draft, resp.data.updatedAt)) {
        if (draft.metaForm) {
          setMetaForm(draft.metaForm);
          setMetaDirty(true);
        }
        if (draft.formData) {
          setFormData(draft.formData);
          setPlanDirty(true);
        }
        if (draft.executionFormData) {
          setExecutionFormData(draft.executionFormData);
          setExecutionDirty(true);
        }
        setMessage(tr("已恢复登录超时前未保存的内容，请检查后保存。"));
      }
    } catch (e) {
      console.log(e);
      setPlan(null);
      setMessage(e?.response?.data?.message || tr("加载课程设计详情失败。"));
    } finally {
      setIsLoadingPlan(false);
    }
  }, [planId]);

  useEffect(() => {
    retrievePlan();
  }, [retrievePlan]);

  // 欣欣小助手 changed this plan (see copilot-panel.component.js#announceChanges)
  // -- reload so the page shows it, unless there are unsaved edits here:
  // planFormData/executionFormData are saved wholesale, so silently
  // reloading would discard them, and saving them would overwrite the
  // assistant's change -- the teacher gets told and decides.
  useEffect(() => {
    const onCopilotChange = (e) => {
      const ids = (e.detail && e.detail.planIds) || [];
      if (!ids.some((id) => String(id) === String(planId))) return;
      if (e.detail.deleted) {
        const user = AuthService.getCurrentUser();
        const ownedByMe = plan && user && String(plan.teacherId) === String(user.id);
        props.history.push(ownedByMe ? "/plans?mine=true" : "/plans");
        return;
      }
      if (planDirty || executionDirty || metaDirty) {
        setMessage(tr("欣欣小助手已修改了本课程设计，但当前页面有未保存的修改：保存将覆盖助手的修改；如需查看助手的修改，请放弃当前修改并刷新页面。"));
        return;
      }
      retrievePlan();
    };
    window.addEventListener("copilot:data-changed", onCopilotChange);
    return () => window.removeEventListener("copilot:data-changed", onCopilotChange);
  }, [planId, plan, planDirty, executionDirty, metaDirty, retrievePlan, props.history]);

  // Unsaved edits in an expert's review forms (utils/pendingReviewEdits.js),
  // including ones in a section that isn't on screen -- guarded by the same
  // beforeunload/<Prompt> as the plan's own edits. Leaving the page after
  // that prompt discards them, just like the plan's own.
  const [reviewEditsPending, setReviewEditsPending] = useState(() => hasPendingReviewEdits(planId));
  useEffect(() => {
    setReviewEditsPending(hasPendingReviewEdits(planId));
    const unsubscribe = subscribePendingReviewEdits(() => setReviewEditsPending(hasPendingReviewEdits(planId)));
    return () => {
      unsubscribe();
      discardPendingReviewEdits(planId);
    };
  }, [planId]);

  // Covers actual tab close/refresh/typed-URL navigation -- the in-app
  // <Prompt> below (same planDirty/executionDirty/metaDirty condition) covers
  // react-router navigation (返回, browser back/forward) instead, since
  // beforeunload doesn't fire for client-side route changes.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!planDirty && !executionDirty && !metaDirty && !reviewEditsPending) return;
      // Set by e.g. App.js's logOut right before a reload it already got
      // explicit confirmation for via its own push-triggered <Prompt> --
      // this component isn't guaranteed to have unmounted (and torn down
      // this very listener) synchronously by that point, so without this
      // check the same question would get asked a second, redundant time.
      if (consumeSkipUnsavedWarning()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [planDirty, executionDirty, metaDirty, reviewEditsPending]);

  const currentUser = AuthService.getCurrentUser();
  const isOwner = !!(plan && currentUser && String(plan.teacherId) === String(currentUser.id));

  // Session-timeout auto-save (see utils/sessionExpiryGuard.js). Reads the
  // latest edits through a ref, updated every render, so the handler is
  // registered once per plan rather than re-registered on every keystroke.
  // Each dirty part is saved as a draft (no `status`, so a submitted plan
  // stays submitted) in its own request, so e.g. an invalid 基本信息 field
  // can't block the lesson content from saving; whatever fails stays in the
  // local draft.
  const expiryStateRef = useRef({});
  expiryStateRef.current = {
    canEdit: isOwner && !(plan && plan.suspended),
    userId: currentUser && currentUser.id,
    metaForm,
    formData,
    executionFormData,
    metaDirty,
    planDirty,
    executionDirty,
  };
  // The forms' live (possibly unsaved) answers, for 欣欣小助手's 小助手 menus
  // (AskAiMenu) -- read when a question is sent, not when the menu rendered,
  // so a follow-up question sees edits made in between.
  const liveFormRef = useRef({});
  liveFormRef.current = { formData, executionFormData, metaForm };
  useEffect(() => {
    const unsavedParts = () => {
      const s = expiryStateRef.current;
      if (!s.canEdit || !s.userId) return null;
      const parts = {};
      if (s.metaDirty && s.metaForm) parts.metaForm = s.metaForm;
      if (s.planDirty) parts.formData = s.formData;
      if (s.executionDirty) parts.executionFormData = s.executionFormData;
      return Object.keys(parts).length ? parts : null;
    };
    return registerSessionExpiryHandler({
      hasUnsaved: () => !!unsavedParts(),
      stash: () => {
        const parts = unsavedParts();
        if (parts) writePlanDraft(planId, expiryStateRef.current.userId, parts);
      },
      save: async () => {
        const parts = unsavedParts();
        if (!parts) return;
        const s = expiryStateRef.current;
        const remaining = { ...parts };
        const attempt = async (key, payload, markClean) => {
          try {
            await PlanDataService.update(planId, payload);
            delete remaining[key];
            s[markClean] = false;
          } catch (e) {
            console.log(e);
          }
        };
        if (parts.metaForm) await attempt("metaForm", metaFormPayload(parts.metaForm), "metaDirty");
        if (parts.formData) await attempt("formData", { planFormData: parts.formData }, "planDirty");
        if (parts.executionFormData) await attempt("executionFormData", { executionFormData: parts.executionFormData }, "executionDirty");
        if (Object.keys(remaining).length) writePlanDraft(planId, s.userId, remaining);
        else takePlanDraft(planId, s.userId);
        setMetaDirty(s.metaDirty);
        setPlanDirty(s.planDirty);
        setExecutionDirty(s.executionDirty);
      },
    });
  }, [planId]);

  // 欣欣小助手 reads the plan from the server, so unsaved edits get saved
  // (as a draft, like 保存草稿) before a 小助手 menu opens the panel and
  // before each question is sent -- otherwise it answers about stale
  // content. One request, same as saveAll. A part edited again while the
  // save was in flight stays dirty. The plan is re-read quietly (no
  // 加载中 flash) so e.g. a changed 预计课时 reaches the 课时 nav.
  useEffect(
    () =>
      registerCopilotSaveHandler(async () => {
        const s = expiryStateRef.current;
        if (!s.canEdit) return true;
        const payload = {};
        if (s.metaDirty && s.metaForm) Object.assign(payload, metaFormPayload(s.metaForm));
        if (s.planDirty) payload.planFormData = s.formData;
        if (s.executionDirty) payload.executionFormData = s.executionFormData;
        if (Object.keys(payload).length === 0) return true;
        const sent = { metaForm: s.metaForm, formData: s.formData, executionFormData: s.executionFormData };
        try {
          await PlanDataService.update(planId, payload);
        } catch (err) {
          setMessage(tr("向欣欣小助手提问前自动保存失败：{0}", { "0": err?.response?.data?.message || tr("保存失败。") }));
          return false;
        }
        const now = expiryStateRef.current;
        if (now.metaForm === sent.metaForm) setMetaDirty(false);
        if (now.formData === sent.formData) setPlanDirty(false);
        if (now.executionFormData === sent.executionFormData) setExecutionDirty(false);
        setMessage(tr("已自动保存，欣欣小助手将基于保存后的内容回答。"));
        PlanDataService.get(planId)
          .then((resp) => setPlan(resp.data))
          .catch(() => {});
        return true;
      }),
    [planId]
  );
  const isAdmin = AuthService.isAdmin();
  // Always editable for the owner/admin, regardless of status (submitting for
  // review no longer locks the plan) -- previously gated on plan.status ===
  // "draft", which made it read-only the moment a teacher clicked 提交待点评.
  // Editing a plan's content is owner-only, no admin bypass -- managers can
  // suspend/promote/leave notes (see the 管理员操作 card below) but not edit
  // case content, even one they don't own (matches plan.controller.js#update,
  // which enforces the same rule server-side). A suspended plan is
  // additionally locked against edits even for its owner, until an admin
  // unsuspends it.
  const canEditPlan = isOwner && !(plan && plan.suspended);
  // 支撑材料 download is opened up to admins ("manager", the seeded admin
  // account's persona -- see role.model.js) reviewing a plan they don't own,
  // unlike upload/move/delete which stay owner-only via canEditPlan above.
  const canDownloadPlan = canEditPlan || isAdmin;
  // 请AI点评: the owner (canEditPlan), or an admin/expert once the plan is
  // submitted and not suspended -- mirrors review.controller.js#
  // createAiReview's server-side check.
  const canTriggerAiReview =
    canEditPlan || ((isAdmin || AuthService.isExpert()) && !!plan && plan.status !== "draft" && !plan.suspended);

  // Whether each 整体点评 view holds a review the plan's OWNER teacher hasn't
  // seen yet (review.model.js's teacherSeenAt) -- flashes that sidebar leaf.
  // Only the owner's own viewing counts: an admin or expert opening the
  // plan neither sees the flash nor clears it (the server's markSeen is a
  // no-op for them too). Scoped with the same scopeReviews the ReviewList
  // itself uses, so a flash always points at a review that view shows.
  const [unseenReviews, setUnseenReviews] = useState({ design: false, implementation: false });
  const refreshUnseenReviews = useCallback(() => {
    if (!isOwner) return;
    ReviewDataService.getByPlan(planId)
      .then((resp) => {
        const unseen = (Array.isArray(resp.data) ? resp.data : []).filter((r) => !r.teacherSeenAt);
        const has = (aggregateScope) => scopeReviews(unseen, { aggregateScope, sectionLabels: planSectionLabels }).length > 0;
        setUnseenReviews({ design: has("design"), implementation: has("implementation") });
      })
      .catch(() => {});
  }, [isOwner, planId, planSectionLabels]);
  useEffect(() => {
    refreshUnseenReviews();
  }, [refreshUnseenReviews]);
  // A review can arrive while the teacher has this page open (an expert's
  // comment, a batch's AI 点评) -- re-check whenever they come back to the
  // tab, so the flash doesn't wait for a reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUnseenReviews();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshUnseenReviews]);
  // The flash must stay visible however the nav is folded: a collapsed
  // 计划/实施 folder, or the whole nav hidden, carries its leaf's signal.
  const unseenAny = unseenReviews.design || unseenReviews.implementation;

  // Returns to wherever the user actually came from (browser/react-router
  // POP), rather than a fixed destination -- every entry point that links
  // here (plan-card.component.js, used by both plans-list's grid and
  // plans-hierarchy's tree; template-admin.component.js's/
  // admin-users-list.component.js's own plan-link popups) already pushed a
  // fresh history entry on top of whatever page the user was actually on,
  // so a real goBack() lands back on that page -- 用户管理's 学校信息 panel
  // for an admin who got here via a school's course list, 模板管理 for one
  // who got here via a template's dependent-plan list, etc. -- instead of
  // always the same hardcoded 全部乡土课程/我的乡土课程, which was only ever
  // a coincidentally-right guess for the one entry point (the plans list)
  // this button originally had to support.
  //
  // window.history.length > 1 is the fallback-safety check: it stays 1 for
  // a plan opened with no prior in-app (or same-tab) history at all -- a
  // fresh tab, a pasted/bookmarked URL, or a page refresh's very first
  // load -- where goBack() would otherwise leave the app entirely (to
  // about:blank or wherever the tab was before). Switching between this
  // page's own WHY/WHAT/HOW/课时 tabs is local component state (see
  // `select` above), not a route push, so it never inflates this count on
  // its own.
  //
  // On a phone with a section open, this page's own ?pane=content entry
  // (explorer-pane.js) sits on top -- pane.backSteps pops past it too.
  const goBack = () => {
    if (window.history.length > 1) {
      props.history.go(-pane.backSteps);
      return;
    }
    props.history.push(isOwner ? "/plans?mine=true" : "/plans");
  };

  const updateMetaForm = (patch) => {
    setMetaForm((prev) => ({ ...prev, ...patch }));
    setMetaDirty(true);
  };

  // Backs the page's single 保存草稿/提交待点评 pair: everything dirty
  // (基本信息, formData, executionFormData) goes out in ONE update request,
  // so the save is all-or-nothing server-side (plan.controller.js#update
  // runs in a transaction) -- no half-saved state where, say, an invalid
  // 标题 rejects 基本信息 while the lesson content went through. Clean parts
  // are left out of the payload, same as before, so segmentVersionAt only
  // ever moves for what actually changed.
  const saveAll = async (submitStatus) => {
    const payload = {};
    if (metaDirty) Object.assign(payload, metaFormPayload(metaForm));
    if (planDirty) payload.planFormData = formData;
    if (executionDirty) payload.executionFormData = executionFormData;
    if (submitStatus) payload.status = submitStatus;
    if (Object.keys(payload).length === 0) return;
    try {
      await PlanDataService.update(planId, payload);
      setMetaDirty(false);
      setPlanDirty(false);
      setExecutionDirty(false);
      setMessage(submitStatus === "submitted" ? tr("课程设计已提交待点评。") : tr("已保存。"));
      retrievePlan();
    } catch (err) {
      setMessage(err?.response?.data?.message || tr("保存失败。"));
    }
  };

  // Mirrors mergeFormData's multi-vs-single-section branching: nested-by-
  // section-key when the plan's schema has more than one section (the seed
  // WHY/WHAT/HOW), flat when it has exactly one (any future re-uploaded
  // template collapses to one -- see the dynamic-templates plan).
  const onFormFieldChange = (sectionKey, field, value) => {
    const sections = (plan && plan.PlanTemplateVersion && plan.PlanTemplateVersion.schemaJson && plan.PlanTemplateVersion.schemaJson.sections) || [];
    setFormData((prev) =>
      sections.length > 1 ? { ...prev, [sectionKey]: { ...prev[sectionKey], [field]: value } } : { ...prev, [field]: value }
    );
    setPlanDirty(true);
  };

  // formData.lessons is a sparse array of { index, title, content } (see
  // EMPTY_LESSON) -- index n may have no entry yet (a plan with no lesson
  // content filled in, or a lesson beyond what's been written so far), so this
  // creates one on first edit rather than requiring every lessonCount slot to
  // be pre-populated up front.
  const onLessonFieldChange = (lessonIndex, field, value) => {
    setFormData((prev) => {
      const lessons = prev.lessons.some((l) => Number(l.index) === lessonIndex)
        ? prev.lessons.map((l) => (Number(l.index) === lessonIndex ? { ...l, [field]: value } : l))
        : [...prev.lessons, { ...EMPTY_LESSON, index: lessonIndex, [field]: value }];
      return { ...prev, lessons };
    });
    setPlanDirty(true);
  };

  // executionFormData is a sparse array of 实施记录 entries, same shape as
  // formData.lessons (see onLessonFieldChange above) -- creates an entry on
  // first edit rather than requiring every lesson slot pre-populated. No
  // base "empty record" to spread in (unlike before this became
  // schema-driven) -- a field missing from a fresh entry just isn't in the
  // object yet, which DynamicSectionFields already renders as "" (see its
  // `values && values[field.key]) || ""`).
  const onExecutionFieldChange = (lessonIndex, field, value) => {
    setExecutionFormData((prev) =>
      prev.some((r) => Number(r.index) === lessonIndex)
        ? prev.map((r) => (Number(r.index) === lessonIndex ? { ...r, [field]: value } : r))
        : [...prev, { index: lessonIndex, [field]: value }]
    );
    setExecutionDirty(true);
  };

  // Clears the migration leftovers at formData._manualMigration (see
  // plan.controller.js#removeManualMigration) once the owner has manually
  // copied over whatever they still needed -- the nav leaf and its flashing
  // style disappear immediately afterward since retrievePlan() re-derives
  // formData from the now-empty server state.
  const handleRemoveManualMigration = async () => {
    if (!window.confirm(tr("确定删除手动迁移板块吗？此操作无法撤销。"))) return;
    try {
      await PlanDataService.removeManualMigration(planId);
      select("plan", "basic");
      retrievePlan();
    } catch (err) {
      setMessage(err?.response?.data?.message || tr("删除手动迁移板块失败。"));
    }
  };

  // Shared by both doc-upload panels below (DesignDocPanel/
  // LessonExecutionDocPanel), called once their own upload request (which
  // now goes straight to plan.controller.js#uploadDesignDoc/
  // uploadExecutionDoc -- see PlanDataService.uploadDesignDoc/
  // uploadExecutionDoc -- and already persisted its own domain server-side)
  // has succeeded. `justUploadedDomain` is "plan" or "execution" -- per the
  // user's requirement, this still also flushes whatever *other* domain is
  // currently dirty in memory, so an upload never silently strands unsaved
  // edits sitting elsewhere on the page; unlike the old onUploadReplace this
  // is necessarily a second request now (the new upload endpoints only
  // accept the file itself), not the same one, but the net effect -- both
  // domains end up saved -- is unchanged.
  const onUploadComplete = async (justUploadedDomain) => {
    const otherPayload = {};
    if (justUploadedDomain !== "plan" && planDirty) otherPayload.planFormData = formData;
    if (justUploadedDomain !== "execution" && executionDirty) otherPayload.executionFormData = executionFormData;
    if (Object.keys(otherPayload).length > 0) {
      await PlanDataService.update(planId, otherPayload);
    }
    setPlanDirty(false);
    setExecutionDirty(false);
    retrievePlan();
  };

  const toggleExcellent = async () => {
    try {
      await PlanDataService.update(planId, { isExcellentCase: !plan.isExcellentCase });
      retrievePlan();
    } catch (err) {
      setMessage(err?.response?.data?.message || tr("操作失败。"));
    }
  };

  const toggleSuspend = async () => {
    if (!plan.suspended) {
      const ok = window.confirm(tr("确定停用「{plan.title}」吗？停用后该课程设计将从公开列表中隐藏，仅本人与管理员可见。", { "plan.title": plan.title }));
      if (!ok) return;
    }
    try {
      if (plan.suspended) {
        await PlanDataService.unsuspend(planId);
      } else {
        await PlanDataService.suspend(planId);
      }
      retrievePlan();
    } catch (err) {
      setMessage(err?.response?.data?.message || tr("操作失败。"));
    }
  };

  if (isLoadingPlan) {
    return (
      <div className="container pl-page">
        <div className="pl-empty">{tr("加载中...")}</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="container pl-page">
        <div className="alert alert-danger py-2 mb-0">{message || tr("加载课程设计详情失败。")}</div>
      </div>
    );
  }

  const lessonCount = plan.plannedLessonCount || 0;
  const lessons = Array.from({ length: lessonCount }, (_, i) => i + 1);
  const planTemplateSchema = (plan.PlanTemplateVersion && plan.PlanTemplateVersion.schemaJson) || { sections: [] };
  const planSchemaMultiSection = planTemplateSchema.sections.length > 1;
  // The sidebar's WHY/WHAT/HOW-equivalent pages and their review blocks are
  // built from these, not planTemplateSchema.sections directly -- see
  // anchorSections' comment.
  const planAnchorSections = anchorSections(planTemplateSchema);
  // Hoisted here (rather than locally inside the executionRecord branch
  // below) so it's available for executionNotEmpty too.
  const executionTemplateSchema = (plan.ExecutionTemplateVersion && plan.ExecutionTemplateVersion.schemaJson) || { sections: [] };
  // "Not empty" gate for 提交待点评 -- reuses hasAnySectionContent, the same
  // "did the user actually write anything" check already used for
  // upload-extraction (see DesignDocPanel/LessonExecutionDocPanel below).
  const planNotEmpty =
    hasAnySectionContent(planTemplateSchema, formData) ||
    (formData.lessons || []).some((l) =>
      planTemplateSchema.lessonSchema
        ? Object.keys(l).some((k) => k !== "index" && l[k] != null && String(l[k]).trim() !== "")
        : (l.title || "").trim() || (l.content || "").trim()
    );
  const executionNotEmpty = executionFormData.some((r) => hasAnySectionContent(executionTemplateSchema, r));

  // AI已点评/专家已点评 are derived from plan.Reviews (already fetched with this
  // plan, see plan.controller.js#findOne), not separate stored flags -- "has
  // this plan received at least one review of that reviewerType". "专家"
  // means reviewerType==='expert' specifically, not 'admin' (see
  // review.model.js's own comment on why those stay distinct).
  const planReviews = plan.Reviews || [];
  const expertReviews = planReviews.filter((r) => r.reviewerType === "expert");
  // 小助手 menu entries by the user's relationship to this plan -- its
  // author gets writing help, an expert reviewing it gets review help (the
  // backend gets the same relationship, see copilotFocus.js).
  const askAiPresetList = askAiPresets({ isAuthor: isOwner, isExpert: AuthService.isExpert(), isAdmin });
  const sectionHeadingAskAi = (kind, label, extra, getDraft) => (
    <AskAiMenu presets={askAiPresetList} unit={tr("这一部分")} target={{ kind, ...extra, labelPath: [label], getDraft }} />
  );

  // 基本信息's own 小助手 menus -- the section heading plus 标题 / 乡土主题,
  // where its suggestions land (see basicInfoPresets).
  const basicRole = { isAuthor: isOwner, isExpert: AuthService.isExpert(), isAdmin };
  const basicDraft = () => {
    const m = liveFormRef.current.metaForm || {};
    return BASIC_INFO_LABELS.filter(([key]) => m[key] !== undefined && m[key] !== null && String(m[key]).trim() !== "")
      .map(([key, label]) => `【${label}】${m[key]}`)
      .join("\n");
  };
  const basicFieldAskAi = (fieldKey, label, keys) => (
    <AskAiMenu
      presets={basicInfoPresets(basicRole, keys)}
      target={{ kind: "planBasic", fieldKey, labelPath: [tr("基本信息"), label], getDraft: () => String((liveFormRef.current.metaForm || {})[fieldKey] ?? "") }}
    />
  );

  const reviewFlags = {
    aiReviewed: planReviews.some((r) => r.reviewerType === "ai"),
    expertReviewed: expertReviews.length > 0,
    expertReviewerNames: [...new Set(expertReviews.map((r) => (r.Reviewer ? r.Reviewer.chineseName || r.Reviewer.username : tr("专家"))))],
  };

  const renderContent = () => {
    if (selected.type === "plan" && selected.key === "basic") {
      return (
        <div className="pl-card">
          <h6 className="ai-ask-host">
            {tr("基本信息")}
            <AskAiMenu presets={basicInfoPresets(basicRole)} unit={tr("这一部分")} target={{ kind: "planBasic", labelPath: [tr("基本信息")], getDraft: basicDraft }} />
          </h6>
          <div className="mb-3">
            <span className={`pl-plan-card-status status-${plan.status || "draft"}`}>
              {(PLAN_STATUSES.find((s) => s.value === plan.status) || PLAN_STATUSES[0]).label}
            </span>
            {reviewFlags.aiReviewed && <span className="pl-tag-ai ml-2">{tr("AI已点评")}</span>}
            {reviewFlags.expertReviewed && <span className="pl-tag-expert ml-2">{tr("专家已点评")}</span>}
            {reviewFlags.expertReviewed && (
              <span className="text-muted ml-2" style={{ fontSize: "12px" }}>
                {tr("（")}{reviewFlags.expertReviewerNames.join(tr("、"))}{tr("）")}
              </span>
            )}
          </div>
          {/* No button of its own -- saved by the page-wide 保存草稿 in the
              sticky header (see saveAll); Enter in a field does the same. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveAll();
            }}
          >
            <div className="form-group ai-ask-host">
              <label>{tr("标题")}</label>
              {basicFieldAskAi("title", tr("标题"), ["suggestTitles", "checkMatch"])}
              {/* textarea (not a single-line input) so a long 标题 (project
                  titles here routinely run past what a single-line input can
                  show, e.g. "伞韵米香·寻味五溪——...") wraps and stays fully
                  visible instead of scrolling off sideways; overflowY: auto
                  caps its growth and scrolls internally past maxHeight rather
                  than pushing the rest of the form down indefinitely. */}
              <textarea
                className="form-control"
                rows={2}
                style={{ resize: "vertical", overflowY: "auto", maxHeight: "150px" }}
                value={metaForm.title}
                onChange={(e) => updateMetaForm({ title: e.target.value })}
                disabled={!canEditPlan}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group col-md-3">
                <label>{tr("年份")}</label>
                <input
                  className="form-control"
                  type="number"
                  value={metaForm.year}
                  onChange={(e) => updateMetaForm({ year: e.target.value })}
                  disabled={!canEditPlan}
                  required
                />
              </div>
              <div className="form-group col-md-3">
                <label>{tr("学季")}</label>
                <select className="form-control" value={metaForm.season} onChange={(e) => updateMetaForm({ season: e.target.value })} disabled={!canEditPlan}>
                  {PLAN_SEASONS.map((s) => (
                    <option key={s} value={s}>
                      {tr(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-3 ai-ask-host">
                <label>{tr("乡土主题")}</label>
                {basicFieldAskAi("theme", tr("乡土主题"), ["suggestThemes", "suggestTitles"])}
                <select className="form-control" value={metaForm.theme} onChange={(e) => updateMetaForm({ theme: e.target.value })} disabled={!canEditPlan}>
                  <option value="">{tr("不限")}</option>
                  {themeOptions.map((t) => (
                    <option key={t} value={t}>
                      {tr(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-3">
                <label>{tr("年级")}</label>
                <select className="form-control" value={metaForm.grade} onChange={(e) => updateMetaForm({ grade: e.target.value })} disabled={!canEditPlan}>
                  <option value="">{tr("不限")}</option>
                  {PLAN_GRADES.map((g) => (
                    <option key={g} value={g}>
                      {tr(g)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-4">
                <label>{tr("学生人数")}</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  value={metaForm.studentCount}
                  onChange={(e) => updateMetaForm({ studentCount: e.target.value })}
                  disabled={!canEditPlan}
                />
              </div>
              <div className="form-group col-md-4">
                <label>{tr("执教人")}</label>
                <input
                  className="form-control"
                  type="text"
                  value={metaForm.instructorName}
                  onChange={(e) => updateMetaForm({ instructorName: e.target.value })}
                  disabled={!canEditPlan}
                />
              </div>
              <div className="form-group col-md-4">
                <label>{tr("预计课时")}</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  max="60"
                  value={metaForm.plannedLessonCount}
                  onChange={(e) => updateMetaForm({ plannedLessonCount: e.target.value })}
                  disabled={!canEditPlan}
                />
              </div>
            </div>
          </form>
        </div>
      );
    }

    // WHY/WHAT/HOW-equivalent -- one generic branch driven by whichever
    // sections plan.PlanTemplateVersion.schemaJson has (at the anchoring
    // level -- see anchorSections), replacing what used to be three separate
    // hand-written branches each hardcoding their own field list.
    // selected.key is an anchor section's key (e.g. "why"/"what"/"how" for
    // the seed template, the heading-parsed WHY/WHAT/HOW's own auto-assigned
    // keys for a re-uploaded one, or "main" for a table/flat-parsed one).
    if (selected.type === "planSection") {
      const section = planAnchorSections.find((s) => s.key === selected.key);
      if (!section) return null;
      const values = planSchemaMultiSection ? formData[section.key] || {} : formData;
      const getValues = () => {
        const live = liveFormRef.current.formData || {};
        return planSchemaMultiSection ? live[section.key] || {} : live;
      };
      return (
        <div className="pl-card pl-why-what-how">
          <h6 className="ai-ask-host">
            {section.label}
            {sectionHeadingAskAi("planSection", section.label, { sectionKey: section.key }, () =>
              partDraftText(directFields(section), section.subsections, getValues())
            )}
          </h6>
          <DynamicSectionFields
            fields={directFields(section)}
            subsections={section.subsections}
            values={values}
            canEdit={canEditPlan}
            onFieldChange={(field, value) => onFormFieldChange(section.key, field, value)}
            askAi={{ kind: "planSection", sectionKey: section.key, basePath: [section.label], presets: askAiPresetList, getValues }}
          />
          <hr />
          <ReviewList
            planId={planId}
            lessonIndex={null}
            sectionKey={section.key.toUpperCase()}
            sectionLabels={planSectionLabels}
            embedded
            planContentVersionAt={plan.contentVersionAt}
            segmentVersionAt={plan.segmentVersionAt}
          />
        </div>
      );
    }

    // Leftover content templateMigration.js#migratePlanFormData couldn't
    // place on the new schema (no matching field by label) -- read-only,
    // and deliberately whole-block-or-nothing (see handleRemoveManualMigration):
    // there's no per-entry edit here, only "copy whatever you still need
    // into the real fields yourself, then remove this block."
    if (selected.type === "planManualMigration") {
      const entries = formData._manualMigration || [];
      return (
        <div className="pl-card">
          <h6>{tr("手动迁移内容")}</h6>
          <p className="text-muted">
            {tr("以下内容来自旧版模板，新版模板中没有对应字段，请手动将需要保留的内容复制到相应板块后删除本板块。本板块不会包含在下载的文档中。")}
          </p>
          {entries.map((entry, idx) => (
            <div key={idx} className="pl-card mb-2">
              <div className="font-weight-bold">{entry.group ? `${entry.group} · ${entry.label}` : entry.label}</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{entry.value}</div>
            </div>
          ))}
          {canEditPlan && (
            <button className="btn btn-outline-danger" type="button" onClick={handleRemoveManualMigration}>
              {tr("删除本板块")}
            </button>
          )}
        </div>
      );
    }

    if (selected.type === "plan" && selected.key === "files") {
      return (
        <div className="pl-card">
          <div className="mb-2">
            <h6 className="mb-0">{tr("课程设计文件")}</h6>
          </div>
          <DesignDocPanel planId={planId} plan={plan} canEdit={canEditPlan} onUploadComplete={onUploadComplete} />
        </div>
      );
    }

    if (selected.type === "plan" && selected.key === "reviews") {
      return (
        <div className="pl-card">
          <h6>{tr("计划整体点评")}</h6>
          <ReviewList
            planId={planId}
            lessonIndex={null}
            sectionLabels={planSectionLabels}
            embedded
            planContentVersionAt={plan.contentVersionAt}
            segmentVersionAt={plan.segmentVersionAt}
            canTriggerAi={canTriggerAiReview}
            canDiscussAi={canEditPlan}
            aiPending={aiReviewPending.design}
            setAiPending={(v) => setAiReviewPending((prev) => ({ ...prev, design: v }))}
            trackSeen={isOwner}
            onSeen={refreshUnseenReviews}
            // Lets the aggregate view's 模块 column jump straight to that
            // section's own tab. "LESSON_DESIGN" rows carry their lessonIndex
            // and route to that lesson's own tab; everything else is a
            // schema section key stored upper-cased (see the "planSection"
            // branch's own ReviewList, which passes section.key.toUpperCase()),
            // so this reverses that to match the schema's actual key.
            onSelectSection={(key, lessonIdx) =>
              key === "LESSON_DESIGN" ? select("planLesson", lessonIdx) : select("planSection", key.toLowerCase())
            }
          />
        </div>
      );
    }

    // "第二部分：分课时设计" in the template is part of the *design* document (课程设计
    // 方案) -- the teacher's planned title/content for each 课时 -- not a record of
    // what actually happened in class. That's why it lives under 计划's own
    // "分课时设计" sub-tree (see the sidebar below) rather than inside 实施's 课时 N
    // panes, which are for actual delivery evidence (uploaded artifacts, reviews).
    if (selected.type === "planLesson") {
      const n = selected.key;
      // lessons is a sparse array (see onLessonFieldChange), so a lesson with
      // nothing written yet falls back to EMPTY_LESSON (harmless even when
      // lessonSchema is present -- its unused title/content keys are just
      // ignored by DynamicSectionFields, which only reads its own field keys).
      const lesson = formData.lessons.find((l) => Number(l.index) === n) || EMPTY_LESSON;
      const lessonSchema = planTemplateSchema.lessonSchema;
      const lessonLabel = tr("分课时设计 · 课时 {n}", { n });
      const getLessonValues = () => ((liveFormRef.current.formData || {}).lessons || []).find((l) => Number(l.index) === n) || {};
      return (
        <div className="pl-card pl-why-what-how">
          <h6 className="ai-ask-host">
            {lessonLabel}
            {sectionHeadingAskAi("planLesson", lessonLabel, { lessonIndex: n }, () =>
              lessonSchema
                ? partDraftText(lessonSchema.fields, lessonSchema.subsections, getLessonValues())
                : partDraftText(FREEFORM_LESSON_FIELDS, null, getLessonValues())
            )}
          </h6>
          {lessonSchema ? (
            // Schema-driven: a reusable per-课时 field template extracted
            // from the source template itself (see templateParser.js#
            // extractLessonSchema) -- applied once per lesson index, same
            // convention as the executionRecord branch below.
            <DynamicSectionFields
              fields={lessonSchema.fields}
              subsections={lessonSchema.subsections}
              values={lesson}
              canEdit={canEditPlan}
              onFieldChange={(field, value) => onLessonFieldChange(n, field, value)}
              askAi={{ kind: "planLesson", lessonIndex: n, basePath: [lessonLabel], presets: askAiPresetList, getValues: getLessonValues }}
            />
          ) : (
            // Freeform fallback -- every template with no detectable
            // repeating 课时-marker pattern (or parsed before this existed).
            <>
              <div className="form-group">
                <label>{tr("课时标题")}</label>
                <input
                  className="form-control"
                  value={lesson.title}
                  disabled={!canEditPlan}
                  onChange={(e) => onLessonFieldChange(n, "title", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{tr("课时设计内容")}</label>
                <textarea
                  className="form-control"
                  rows="10"
                  value={lesson.content}
                  disabled={!canEditPlan}
                  onChange={(e) => onLessonFieldChange(n, "content", e.target.value)}
                />
              </div>
            </>
          )}
          <hr />
          <ReviewList
            planId={planId}
            lessonIndex={n}
            sectionKey="LESSON_DESIGN"
            embedded
            planContentVersionAt={plan.contentVersionAt}
            segmentVersionAt={plan.segmentVersionAt}
          />
        </div>
      );
    }

    // 课时N under 实施 splits into four leaves (see the sidebar below),
    // following the same "online form + on-the-fly 上传/下载/预览 doc panel"
    // pattern 计划's own 课程设计文件 uses -- executionRecord is the form,
    // executionDoc is the doc panel, and the other two are today's
    // LessonFileManager/ReviewList, just no longer combined into one pane.
    if (selected.type === "executionRecord") {
      const n = selected.key;
      const record = executionFormData.find((r) => Number(r.index) === n) || {};
      const section = executionTemplateSchema.sections[0] || { fields: [] };
      const recordLabel = tr("实施记录 · 课时 {n}", { n });
      const getRecordValues = () => (liveFormRef.current.executionFormData || []).find((r) => Number(r.index) === n) || {};
      return (
        <div className="pl-card pl-why-what-how">
          <h6 className="ai-ask-host">
            {recordLabel}
            {sectionHeadingAskAi("executionRecord", recordLabel, { lessonIndex: n }, () =>
              partDraftText(directFields(section), section.subsections, getRecordValues())
            )}
          </h6>
          <DynamicSectionFields
            fields={directFields(section)}
            subsections={section.subsections}
            values={record}
            canEdit={canEditPlan}
            onFieldChange={(field, value) => onExecutionFieldChange(n, field, value)}
            askAi={{ kind: "executionRecord", lessonIndex: n, basePath: [recordLabel], presets: askAiPresetList, getValues: getRecordValues }}
          />
          <hr />
          <ReviewList
            planId={planId}
            lessonIndex={n}
            sectionKey="EXECUTION_RECORD"
            embedded
            planContentVersionAt={plan.contentVersionAt}
            segmentVersionAt={plan.segmentVersionAt}
          />
        </div>
      );
    }

    if (selected.type === "executionDoc") {
      const n = selected.key;
      return (
        <div className="pl-card">
          <div className="mb-2">
            <h6 className="mb-0">{tr("课程实施文件 · 课时 {n}", { n })}</h6>
          </div>
          <LessonExecutionDocPanel
            planId={planId}
            lessonIndex={n}
            plan={plan}
            canEdit={canEditPlan}
            onUploadComplete={onUploadComplete}
          />
        </div>
      );
    }

    if (selected.type === "execution") {
      const n = selected.key;
      return (
        <div className="pl-card">
          <h6>{tr("支撑材料 · 课时 {n}", { n })}</h6>
          <LessonFileManager
            planId={planId}
            lessonIndex={n}
            canEdit={canEditPlan}
            canDownload={canDownloadPlan}
            onFilesChange={refreshCompletion}
          />
        </div>
      );
    }

    if (selected.type === "executionReviews") {
      return (
        <div className="pl-card">
          <h6>{tr("实施整体点评")}</h6>
          <ReviewList
            planId={planId}
            lessonIndex={null}
            sectionLabels={planSectionLabels}
            aggregateScope="implementation"
            embedded
            planContentVersionAt={plan.contentVersionAt}
            segmentVersionAt={plan.segmentVersionAt}
            // No AI in 实施 for now -- AI 点评 is plan scope (计划整体点评).
            canDiscussAi={canEditPlan}
            aiPending={aiReviewPending.implementation}
            setAiPending={(v) => setAiReviewPending((prev) => ({ ...prev, implementation: v }))}
            trackSeen={isOwner}
            onSeen={refreshUnseenReviews}
            onSelectSection={(key, lessonIdx) =>
              key === "LESSON_DESIGN"
                ? select("planLesson", lessonIdx)
                : key === "EXECUTION_RECORD"
                ? select("executionRecord", lessonIdx)
                : key === "DESIGN_OVERALL"
                ? select("plan", "reviews")
                : select("planSection", key.toLowerCase())
            }
          />
        </div>
      );
    }

    if (selected.type === "admin") {
      return (
        <div className="pl-card">
          <h6>{tr("管理员操作")}</h6>
          <div className="form-group">
            <label>{tr("优秀案例")}</label>
            <div>
              <button className="btn btn-outline-primary btn-sm" type="button" onClick={toggleExcellent}>
                {plan.isExcellentCase ? tr("取消优秀案例标记") : tr("设为优秀案例")}
              </button>
            </div>
          </div>
          <div className="form-group mb-0">
            <label>{tr("停用状态")}</label>
            <div>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={toggleSuspend}>
                {plan.suspended ? tr("启用") : tr("停用")}
              </button>
            </div>
          </div>
          {/* 管理员备注 (curatorNote) retired -- it duplicated 管理员点评 (see
              整体点评/WHY/WHAT/HOW's ReviewList, reviewerType='admin') with
              none of its benefits (no history, no attribution, and it was
              leaking to any viewer via the plan API despite the UI only
              ever showing it here). Leave a 管理员点评 review instead. */}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="container pl-page">
      {/* Covers 返回 above (now itself a goBack()/POP in the common case --
          see its own comment) and any other browser back/forward while
          still on this route -- actual tab close/refresh is the
          beforeunload listener set up above instead. */}
      {/* .some() rather than another `||`: one more branch in this (very
          large) component's body overflows eslint-plugin-react-hooks' code
          path count into false "hook called conditionally" errors. */}
      {/* Switching the phone layout's panes stays on this page -- no prompt. */}
      <Prompt
        when={[planDirty, executionDirty, metaDirty, reviewEditsPending].some(Boolean)}
        message={(next) => isPaneSwitch(props.location.pathname, next) || tr("有未保存的内容，确定要离开吗？")}
      />
      {/* Sticky so 返回 and the page's single 保存草稿/提交待点评 pair stay in
          view however far down a long section the teacher has scrolled --
          that pair saves every section at once (see saveAll), replacing the
          per-section top/bottom copies. The status message lives here too,
          so a save's result is visible without scrolling back up. */}
      <div className="pl-sticky-header" ref={stickyHeaderRef}>
        <div className="pl-hero">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <button type="button" className="btn btn-primary" onClick={goBack}>
              {tr("返回")}
            </button>
            {canEditPlan && (
              <SaveSubmitButtons
                onSaveDraft={() => saveAll()}
                saveDisabled={!planDirty && !executionDirty && !metaDirty}
                onSubmit={() => saveAll("submitted")}
                submitDisabled={!planNotEmpty && !executionNotEmpty}
                showSubmit={plan.status === "draft"}
              />
            )}
          </div>
          <div className="d-flex justify-content-between align-items-end flex-wrap">
            <div>
              <h4 className="pl-title">
                {plan.title}
                {plan.suspended && <span className="pl-tag pl-tag-warn ml-2">{tr("已停用")}</span>}
              </h4>
              <p className="pl-subtitle">
                {tr(plan.theme) || "-"} · {tr(plan.grade) || "-"} {tr("· {plan.year} · 状态：", { "plan.year": plan.year })}{PLAN_STATUS_LABELS[plan.status] || plan.status}
                {plan.isExcellentCase ? tr(" · 优秀案例") : ""}
              </p>
            </div>
            <CompletionBadge completion={plan.completion} />
          </div>
          {plan.suspended && !isAdmin && (
            <div className="alert alert-warning py-2 mb-0">{tr("该课程设计已被管理员停用，如需修改请联系管理员。")}</div>
          )}
        </div>
        {message && (
          <div className="alert alert-info py-2 mt-2 mb-0 d-flex justify-content-between align-items-center">
            <span>{message}</span>
            <button type="button" className="close" aria-label={tr("关闭")} onClick={() => setMessage("")}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}
      </div>

      <div className={`pl-explorer ${pane.className}`}>
        {/* Hide/show the whole nav panel -- distinct from each 计划/实施 group's own
            expand/collapse chevron below. When hidden, the nav is removed entirely
            (not just shrunk) and this handle is the only remaining trace of it. */}
        <button
          type="button"
          className={`pl-explorer-hide-toggle ${navCollapsed && unseenAny ? "pl-flash-unseen" : ""}`}
          onClick={() => setNavCollapsed((prev) => !prev)}
          title={navCollapsed ? (unseenAny ? tr("显示导航（有新的点评）") : tr("显示导航")) : tr("隐藏导航")}
        >
          <i className={`fas fa-${navCollapsed ? "angle-double-right" : "angle-double-left"}`}></i>
          {navCollapsed && unseenAny && <span className="pl-unseen-dot" />}
        </button>

        {!navCollapsed && (
          <div
            className="pl-explorer-nav"
            style={stickyHeaderHeight ? { top: stickyHeaderHeight + 12 } : undefined}
            onClick={pane.onNavClick}
          >
            <div className="pl-explorer-group">
              <button
                type="button"
                className={`pl-explorer-folder ${!expandedGroups.plan && unseenReviews.design ? "pl-flash-unseen" : ""}`}
                title={!expandedGroups.plan && unseenReviews.design ? tr("计划整体点评有新的点评") : undefined}
                onClick={() => {
                  toggleGroup("plan");
                  // 计划 itself has no content of its own -- unlike the other
                  // structural headers (which go blank), clicking it shows
                  // 课程设计文件 directly, matching the 课时N -> 实施记录
                  // shortcut just below in the 实施 group; 课程设计文件 is no
                  // longer its own leaf (see the filtered leaf list below).
                  select("plan", "files");
                }}
              >
                <i className={`fas fa-chevron-${expandedGroups.plan ? "down" : "right"} pl-explorer-chevron`}></i>
                <i className="fas fa-folder-open pl-folder-icon mr-1"></i> {tr("计划")}
                {!expandedGroups.plan && unseenReviews.design && <span className="pl-unseen-dot" />}
              </button>
              {expandedGroups.plan && (
                <div className="pl-explorer-children">
                  <button
                    type="button"
                    className={`pl-explorer-leaf ${selected.type === "plan" && selected.key === "basic" ? "is-active" : ""}`}
                    onClick={() => select("plan", "basic")}
                  >
                    {tr("基本信息")}
                  </button>
                  {/* WHY/WHAT/HOW-equivalent leaves, one per anchoring-level
                      section (see anchorSections and the "planSection"
                      render branch above) -- NOT one per top-level
                      schema.sections entry, which for a heading-parsed
                      template collapses to a single uninformative wrapper
                      (e.g. "课程设计框架") instead of surfacing WHY/WHAT/HOW as
                      their own pages. Online-only, like 分课时设计 just below
                      (an upload-mode plan has no online form to fill in). */}
                  {plan.planMode === "online" &&
                    planAnchorSections.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        className={`pl-explorer-leaf ${selected.type === "planSection" && selected.key === s.key ? "is-active" : ""}`}
                        onClick={() => select("planSection", s.key)}
                      >
                        {s.label}
                      </button>
                    ))}
                  {/* Only present after a migration (plan.controller.js#
                      migrateMine) left old-only content behind -- see
                      templateMigration.js. Flashes (.pl-flash-manual) until
                      the owner removes it, at which point it disappears
                      entirely and the plan reads as "normal" again. */}
                  {formData._manualMigration && formData._manualMigration.length > 0 && (
                    <button
                      type="button"
                      className={`pl-explorer-leaf pl-flash-manual ${
                        selected.type === "planManualMigration" ? "is-active" : ""
                      }`}
                      onClick={() => select("planManualMigration", "manual")}
                    >
                      {tr("手动迁移内容")}
                    </button>
                  )}
                  {/* 第二部分：分课时设计 -- part of the design document (see the
                      planLesson render branch above), so nested here under 计划
                      rather than a sibling of 实施's own 课时 list. Online-only,
                      matching WHY/WHAT/HOW just above. */}
                  {plan.planMode === "online" && (
                    <div className="pl-explorer-subgroup">
                      <button
                        type="button"
                        className="pl-explorer-folder pl-explorer-subfolder"
                        onClick={() => {
                          toggleGroup("planLessons");
                          select("none");
                        }}
                      >
                        <i className={`fas fa-chevron-${expandedGroups.planLessons ? "down" : "right"} pl-explorer-chevron`}></i>
                        {tr("分课时设计")}
                      </button>
                      {expandedGroups.planLessons && (
                        <div className="pl-explorer-children pl-explorer-children-nested">
                          {lessons.map((n) => (
                            <button
                              key={n}
                              type="button"
                              className={`pl-explorer-leaf ${selected.type === "planLesson" && selected.key === n ? "is-active" : ""}`}
                              onClick={() => select("planLesson", n)}
                            >
                              {tr("课时 {n}", { n })}
                            </button>
                          ))}
                          {lessons.length === 0 && <div className="pl-explorer-empty">{tr("尚未设置预计课时")}</div>}
                        </div>
                      )}
                    </div>
                  )}
                  {/* 课程设计文件 ("files") is no longer its own leaf -- clicking
                      计划's own header shows it directly (see that button's
                      onClick above). */}
                  {PLAN_SECTIONS.filter((s) => s.key === "reviews").map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      className={`pl-explorer-leaf ${selected.type === "plan" && selected.key === s.key ? "is-active" : ""} ${
                        unseenReviews.design ? "pl-flash-unseen" : ""
                      }`}
                      title={unseenReviews.design ? tr("有新的点评") : undefined}
                      onClick={() => select("plan", s.key)}
                    >
                      {s.label}
                      {unseenReviews.design && <span className="pl-unseen-dot" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="pl-explorer-group">
              <button
                type="button"
                className={`pl-explorer-folder ${!expandedGroups.execution && unseenReviews.implementation ? "pl-flash-unseen" : ""}`}
                title={!expandedGroups.execution && unseenReviews.implementation ? tr("实施整体点评有新的点评") : undefined}
                onClick={() => {
                  toggleGroup("execution");
                  select("none");
                }}
              >
                <i className={`fas fa-chevron-${expandedGroups.execution ? "down" : "right"} pl-explorer-chevron`}></i>
                <i className="fas fa-folder-open pl-folder-icon mr-1"></i> {tr("实施")}
                {!expandedGroups.execution && unseenReviews.implementation && <span className="pl-unseen-dot" />}
              </button>
              {expandedGroups.execution && (
                <div className="pl-explorer-children">
                  {/* Each 课时N is its own subgroup (same shape as 分课时设计's
                      above): 实施记录 (the online-fill form, with its own
                      embedded 点评 block -- see the executionRecord render
                      branch above), 课程实施文件 (its on-the-fly 上传/下载/
                      预览 doc panel, mirroring 课程设计文件), then 支撑材料
                      (LessonFileManager). No key seeding needed for the
                      dynamic `exec_${n}` toggle -- expandedGroups[key] reads
                      as collapsed (falsy) for any key not yet clicked. */}
                  {lessons.map((n) => (
                    <div className="pl-explorer-subgroup" key={n}>
                      <button
                        type="button"
                        className="pl-explorer-folder pl-explorer-subfolder"
                        onClick={() => {
                          toggleGroup(`exec_${n}`);
                          // 课时N itself has no content of its own -- unlike the other
                          // structural headers above (which just go blank), clicking
                          // this one shows 课程实施文件 directly, matching 计划's own
                          // header -> 课程设计文件 shortcut; 课程实施文件 is no longer
                          // its own leaf (see the leaf list below).
                          select("executionDoc", n);
                        }}
                      >
                        <i className={`fas fa-chevron-${expandedGroups[`exec_${n}`] ? "down" : "right"} pl-explorer-chevron`}></i>
                        {tr("课时 {n}", { n })}
                      </button>
                      {expandedGroups[`exec_${n}`] && (
                        <div className="pl-explorer-children pl-explorer-children-nested">
                          <button
                            type="button"
                            className={`pl-explorer-leaf ${selected.type === "executionRecord" && selected.key === n ? "is-active" : ""}`}
                            onClick={() => select("executionRecord", n)}
                          >
                            {tr("实施记录")}
                          </button>
                          <button
                            type="button"
                            className={`pl-explorer-leaf ${selected.type === "execution" && selected.key === n ? "is-active" : ""}`}
                            onClick={() => select("execution", n)}
                          >
                            {tr("支撑材料")}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {lessons.length === 0 && <div className="pl-explorer-empty">{tr("尚未设置预计课时")}</div>}
                  {/* 实施 section review, as a sibling of the 课时N subgroups --
                      mirrors 计划's own "reviews" leaf above, but aggregates
                      segment reviews from both 设计 and 实施 (see the
                      executionReviews render branch above and
                      review-list.component.js's aggregateScope prop). */}
                  <button
                    type="button"
                    className={`pl-explorer-leaf ${selected.type === "executionReviews" ? "is-active" : ""} ${
                      unseenReviews.implementation ? "pl-flash-unseen" : ""
                    }`}
                    title={unseenReviews.implementation ? tr("有新的点评") : undefined}
                    onClick={() => select("executionReviews")}
                  >
                    {tr("实施整体点评")}
                    {unseenReviews.implementation && <span className="pl-unseen-dot" />}
                  </button>
                </div>
              )}
            </div>

            {isAdmin && (
              <button
                type="button"
                className={`pl-explorer-leaf pl-explorer-top-leaf ${selected.type === "admin" ? "is-active" : ""}`}
                onClick={() => select("admin")}
              >
                <i className="fas fa-cog mr-1"></i> {tr("管理")}
              </button>
            )}
          </div>
        )}

        <div className="pl-explorer-content">
          <ExplorerPaneBar label={paneLabelOf(selected, planAnchorSections)} onBack={pane.openNav} />
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PlanDetail;
